import React from 'react';
import dayjs, { Dayjs } from 'dayjs';

// components
import { DropdownSelectComponent } from 'baseComponents/DropdownSelectComponent';

// styles
import './filterRowValue.scss';

// helpers
import {
  parseAttributeValuesList,
  parseBooleanValueList,
} from 'helpers/dropdown-data';

// store
import { useCreateNewAudienceActions } from 'store/CreateNewAudienceStore';

// assets
import { CREATE_CAMPAIGN_SCREEN } from 'constants/enums/create-campaign-screen';
import { SearchableDropdownSelectComponent } from 'baseComponents/SearchableDropdownSelectComponent';
import { InputNumber } from 'antd';
import { DateTimePickerComponent } from 'baseComponents/DateTimePickerComponent';
import { TextComponent } from 'baseComponents/TextComponent';

interface Props {
  attributeType: string;
  disable: boolean;
  currentFilterInStore: any;
  itemId: string;
  dropdownValues: any[];
  handleSetAttirbuteValueQuery: (value: string) => void;
  loading?: boolean;
  attributeValuesCount: number;
  chosenOperator: string;
  tableReference: string;
}

export const FilterRowValue: React.FC<Props> = (props) => {
  const {
    attributeType,
    disable,
    currentFilterInStore,
    itemId,
    dropdownValues,
    attributeValuesCount,
    chosenOperator,
    tableReference,
  } = props;

  const inputRef = React.useRef<any>(null);

  const { updateValue } = useCreateNewAudienceActions();

  const getUiPresentationValue = (value: any) => {
    const chosenAttributeValue = dropdownValues.find(
      (item) => item.value === value
    );
    return chosenAttributeValue?.ui_presentation || chosenAttributeValue?.value;
  };

  const handleChangeBooleanValue = (value: any) => {
    updateValue(itemId, value, getUiPresentationValue(value));
  };

  const handleChangeTimeValue = (value: Dayjs | null) => {
    if (value) {
      return updateValue(itemId, value.unix());
    }
    updateValue(itemId, null);
  };

  const handleChangeArrayStringValue = (value: string) => {
    if (value) {
      return updateValue(itemId, value, getUiPresentationValue(value));
    }
    updateValue(itemId, null);
  };

  const parsedBooleanValueList = () => {
    if (dropdownValues.length > 0) {
      return parseBooleanValueList(dropdownValues, (item) =>
        handleChangeBooleanValue(item)
      );
    }
    return [];
  };

  const currentFilterValue: any = currentFilterInStore?.value;
  const timePickerValue = () => {
    if (!currentFilterValue) {
      return null;
    }
    if (typeof currentFilterValue === 'number') {
      return dayjs(currentFilterValue * 1000);
    }
    return currentFilterInStore;
  };

  const parsedAttributeValues = () => {
    if (dropdownValues.length > 0) {
      return parseAttributeValuesList(dropdownValues, (item) =>
        handleChangeArrayStringValue(item.value)
      );
    }
    return [];
  };

  const handleTimeAttributeValue = () => {
    if (attributeType === CREATE_CAMPAIGN_SCREEN.TIME_ATTR_TYPE) {
      if (
        chosenOperator === 'less_than_x_days_ago' ||
        chosenOperator === 'greater_than_x_days_ago' ||
        chosenOperator === 'less_than_x_days_in_the_future' ||
        chosenOperator === 'greater_than_x_days_in_the_future'
      ) {
        return (
          <InputNumber
            ref={inputRef}
            disabled={disable}
            onChange={(value) => updateValue(itemId, value)}
            value={currentFilterValue}
          />
        );
      } else {
        return (
          <DateTimePickerComponent
            onChange={(value) => handleChangeTimeValue(value)}
            value={timePickerValue()}
            disabled={disable}
          />
        );
      }
    }
  };

  return (
    <div className='filter-inputs'>
      {handleTimeAttributeValue()}
      {attributeType === CREATE_CAMPAIGN_SCREEN.NUMERIC_ATTR_TYPE &&
        tableReference.length === 0 && (
          <InputNumber
            ref={inputRef}
            disabled={disable}
            onChange={(value) => updateValue(itemId, value)}
            value={currentFilterValue}
          />
        )}
      {attributeType === CREATE_CAMPAIGN_SCREEN.BOOLEAN_ATTR_TYPE && (
        <DropdownSelectComponent
          onChange={() => {}}
          items={parsedBooleanValueList()}
          selectLabelValue={
            getUiPresentationValue(currentFilterValue) ||
            CREATE_CAMPAIGN_SCREEN.CHOOSE_VALUE
          }
          disabled={disable}
        />
      )}
      {(attributeType === CREATE_CAMPAIGN_SCREEN.ARRAY_ATTR_TYPE ||
        attributeType === CREATE_CAMPAIGN_SCREEN.STRING_ATTR_TYPE ||
        (attributeType === CREATE_CAMPAIGN_SCREEN.NUMERIC_ATTR_TYPE &&
          tableReference.length > 0)) && (
        <div className='searchable-dropdown-wrapper'>
          <SearchableDropdownSelectComponent
            multiselect={false}
            onChange={handleChangeArrayStringValue}
            onSearch={props.handleSetAttirbuteValueQuery}
            options={parsedAttributeValues()}
            value={getUiPresentationValue(currentFilterValue) || []}
            placeholder={
              getUiPresentationValue(currentFilterValue) ||
              CREATE_CAMPAIGN_SCREEN.CHOOSE_VALUE
            }
            disabled={disable}
            loading={props.loading}
          />
          {attributeValuesCount > 10 && (
            <div className='count-wrapper'>
              <TextComponent
                size='extra-small'
                opacity
                text={`Total values quantity: ${attributeValuesCount.toString()}`}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
